// My Profile (MOTHER-182): a verified partner changes their own phone and website, over
// PATCH /api/partner/me (MOTHER-181). Names, company and email identify the partner Olytic
// verified, so they are read-only text here. Props only, so tests render it with fake
// data; PartnersPage.tsx shows it at /profile for a verified partner.
import { Button, Card, CardBody, Field, FormGrid, FormGridItem, Inline, InlineMessage, Input, Stack, Text } from '@olytic/foundation';
import { type FormEvent, useState } from 'react';

import type { PartnerMe } from './api';
import { type LeadsView, type ProfileKey, profileChange, type ProfileValues, profileValuesOf } from './flow';
import { BackToMyLeads, LeadsFrame, type LeadPageActions } from './LeadPages';

/** Under each read-only row (PM, 2026-10-29): no mailto, no contact lookup. */
export const READ_ONLY_HELP = 'To change this, contact your Olytic contact.';
const NOT_PROVIDED = 'Not provided';

export interface ProfilePageProps extends LeadPageActions {
  me: PartnerMe;
  view: Extract<LeadsView, { kind: 'profile' }>;
  onSave(values: ProfileValues): void;
  /** Back to the loaded values, sending nothing. */
  onCancel(): void;
}

/** A row the partner cannot change: its value as text, with no input. */
function ReadOnly({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <Field label={label} help={READ_ONLY_HELP}>
      <Text>{value && value.trim() !== '' ? value : NOT_PROVIDED}</Text>
    </Field>
  );
}

/**
 * First Name, Last Name, Company and Email read-only, then Phone and Website with Save and
 * Cancel. Save is disabled until a value changed and while a save runs (`busy`), so a
 * double click sends one request.
 */
export function ProfilePage({ me, view, onSave, onCancel, ...actions }: ProfilePageProps): JSX.Element {
  const loaded = profileValuesOf(me);
  const [values, setValues] = useState<ProfileValues>(() => view.form?.values ?? loaded);
  // A new view (saved, refused, or cancelled) brings its own values: the answer's after a
  // save or a Cancel, what was typed after a refusal.
  const [shown, setShown] = useState(view);
  if (shown !== view) {
    setShown(view);
    setValues(view.form?.values ?? loaded);
  }
  const changed = Object.keys(profileChange(loaded, values)).length > 0;
  const fieldErrors = view.form?.fieldErrors ?? {};
  const partner = me.partner;
  const input = (key: ProfileKey) => ({
    name: key,
    value: values[key],
    onChange: (event: { target: { value: string } }) => setValues((current) => ({ ...current, [key]: event.target.value })),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (actions.busy || !changed) return;
    onSave(values);
  };

  return (
    <LeadsFrame title="My Profile" width="default" breadcrumbs={<BackToMyLeads onOpen={actions.onOpen} />} actions={actions}>
      <Card>
        <CardBody>
          <Stack as="form" gap="md" noValidate onSubmit={submit} aria-label="My Profile">
            {view.notice && !changed ? (
              <InlineMessage tone="success" role="status">
                {view.notice}
              </InlineMessage>
            ) : null}
            {view.form?.error ? (
              <InlineMessage tone="error" role="alert">
                {view.form.error}
              </InlineMessage>
            ) : null}
            <FormGrid columns={2}>
              <ReadOnly label="First Name" value={partner?.firstName} />
              <ReadOnly label="Last Name" value={partner?.lastName} />
              <ReadOnly label="Company" value={partner?.companyName} />
              <ReadOnly label="Email" value={me.email} />
              <Field label="Phone" error={fieldErrors.phone}>
                <Input {...input('phone')} type="tel" />
              </Field>
              <FormGridItem span="full">
                <Field label="Website" error={fieldErrors.companyWebsite}>
                  <Input {...input('companyWebsite')} type="url" />
                </Field>
              </FormGridItem>
            </FormGrid>
            <Inline gap="sm" wrap>
              <Button type="submit" variant="brand" disabled={!changed} loading={actions.busy}>
                {view.form?.retry ? 'Try Again' : 'Save'}
              </Button>
              <Button type="button" variant="neutral" onClick={onCancel} disabled={actions.busy}>
                Cancel
              </Button>
            </Inline>
          </Stack>
        </CardBody>
      </Card>
    </LeadsFrame>
  );
}
